import React, { useState, useEffect, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useHistory } from 'react-router-dom';
import {
  TextField,
  FormControl,
  InputAdornment,
  IconButton,
  FormHelperText,
  Box,
  Checkbox,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { DSLink, DSText } from '@engenharia/ds-react';
import Cookies from 'js-cookie';
import decode from 'jwt-decode';

import api from '../../services/api';
import authApi from '../../services/authApi';
import { redirectUser, loginUser } from '../../services/user';

import { getUrlParam } from '../../utils/params';
import Tip from '../../components/Tip';

import {
  Container,
  Card,
  Divider,
  DividerText,
  DividerLine,
  SignupBox,
  SignInFreeButton,
  LinkButton,
  Image,
  Section,
  CallToActionBox,
  ContainerBanner,
  CheckboxContainer,
} from './styles';

import Logo from '../../components/molecules/Logo';
import Notification from '../../components/Notification';
import MetaHeader from '../../components/MetaHeader';
import {
  mailValidation,
  passwordValidation,
  strongPasswordValidation,
} from '../../utils/validations';
import { getLanguage, setAccountType, setOtpToken } from '../../services/auth';
import { FacebookButton } from '../../components/molecules/FacebookButton';
import { Hidden } from '../../components/utilities/Hidden';
import Button from '../../components/atoms/Button';
import Header from '../../components/Header';
import BlackFriday from '../../components/Banner/BlackFriday';
import { transcodeLanguage } from '../../utils/language';
import { getDomain } from '../../utils/domain';
import Captcha from '../../components/Captcha';
import CircularLoader from '../../components/CircularLoader';

const handleNameCookieWeakPassword = (accountId) => `mlabs-weak-password-${accountId}`;

export default function SignIn() {
  const { t } = useTranslation();
  const history = useHistory();
  const captchaRef = useRef(null);

  const [state, setState] = useState({
    email: '',
    emailValidation: {},
    password: '',
    passwordValidation: {},
    showPassword: false,
    persistent: true,
  });
  const [notification, setNotification] = useState({});
  const [providerNotification, setproviderNotification] = useState({});
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    if (window.ReactNativeWebView && getUrlParam('access_token')) {
      const message = {
        status: 200,
        data: {
          logged: false,
        },
      };

      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  }, []);

  const handlePersistentChange = () => {
    setState((currentState) => ({
      ...currentState,
      persistent: !currentState.persistent,
    }));
  };

  const handleEmailChange = (event) => {
    setState({
      ...state,
      email: event.target.value,
      emailValidation: mailValidation(event.target.value),
    });
  };

  const handlePasswordChange = (event) => {
    setState({
      ...state,
      password: event.target.value,
      passwordValidation: passwordValidation(event.target.value),
    });
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const clickActionButton = async (link) => {
    try {
      // Avoids appending email to link already with an email query
      const url = new URL(link);
      url.searchParams.set('email', state.email);
      const response = await api.get(url.toString());

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    } catch (error) {
      setNotification({
        key: new Date().getTime(),
        message: t('errors.connection-failure'),
      });
    }
  };

  const notificationButton = (options) => (
    <DSLink
      href=""
      onClick={() => clickActionButton(options.link)}
      variant="button"
      color="secondary"
    >
      {options.message}
    </DSLink>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loadingSubmit) {
      return;
    }
    const { email, password, persistent } = state;

    setLoadingSubmit(true);

    try {
      if (!email || !password) {
        setNotification({ key: new Date().getTime(), message: t('errors.login-invalid') });
        return;
      }

      await captchaRef?.current?.reset();
      const captcha = await captchaRef?.current?.executeAsync();

      const response = await authApi.post('/accounts/sign_in', {
        email,
        password,
        callback_url: process.env.REACT_APP_FULL_URL,
        language: transcodeLanguage(getLanguage()),
        captcha,
        persistent,
      });

      const accountId =
        decode(response?.data?.token)?.account_id || decode(response?.data?.token)?.username;
      const otpName = decode(response?.data?.token)?.otp_name;

      if (
        !Cookies.get(handleNameCookieWeakPassword(accountId)) &&
        strongPasswordValidation(password).passwordInvalidError
      ) {
        Cookies.set(handleNameCookieWeakPassword(accountId), false, {
          expires: 30,
          domain: getDomain(),
        });
      }

      if (otpName === undefined || otpName === null) {
        await redirectUser(response);
      } else if (response.data?.token && response.data?.token !== '') {
        setOtpToken(response.data.token);
        history.push('/accounts/verify');
      } else {
        history.push('/accounts/sign_in');
      }
    } catch (error) {
      const { response } = error;

      const actionButton = response?.data?.options && notificationButton(response.data.options);

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
        button: actionButton,
      });

      setLoadingSubmit(false);

      if (response?.data?.options) {
        setState({
          ...state,
          emailValidation: { emailUnconfirmedError: true },
        });
      } else {
        setState({
          ...state,
          emailValidation: { emailInvalidError: true },
          passwordValidation: { passwordInvalidError: true },
        });
      }
    }
  };

  const handleSignUpRedirect = async (event) => {
    event.preventDefault();
    history.push('/start');
  };

  const handleFacebookLogin = async ({ profile, tokenDetail }) => {
    try {
      const response = await authApi.post('/accounts/provider_sign_in', {
        provider: 'facebook',
        user_id: profile.id,
        token: tokenDetail.accessToken,
        name: profile.name,
        email: profile.email,
        captcha: process.env.REACT_APP_CAPTCHA_UUID,
        language: transcodeLanguage(getLanguage()),
      });

      setAccountType('facebook');
      await redirectUser(response);
    } catch (error) {
      const { response } = error;

      const actionButton = response?.data?.options && notificationButton(response.data.options);

      setproviderNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
        button: actionButton,
      });
    }
  };

  function sanitize(message) {
    return sanitizeHtml(message, {
      allowedTags: ['strong', 'a', 'u'],
      allowedAttributes: { a: ['href'] },
    });
  }

  const signUpBox = (
    <SignupBox>
      <DSText variant="subtitle2" color="textSecondary">
        {t('signin.no-account')}
      </DSText>
      <SignInFreeButton
        data-testid="sign-up-navigation-link"
        fullWidth
        onClick={handleSignUpRedirect}
        variant="outlined"
        color="secondary"
      >
        {t('signin.button.signup-free')}
      </SignInFreeButton>
    </SignupBox>
  );

  const logo = <Logo fullWidth imgComponent={Image} />;

  return (
    <>
      <Header positionFixed />
      <Container>
        <ContainerBanner>
          <BlackFriday />
        </ContainerBanner>
        <MetaHeader
          title="Login - mLabs | Gestão de redes sociais"
          description="mLabs - Gestão de Redes Sociais | Entrar"
        />
        <Section>
          <CallToActionBox device="laptop">
            <Box marginBottom={16}>{logo}</Box>
            {signUpBox}
          </CallToActionBox>
          <Card>
            <Hidden invert marginBottom={4} device="laptop">
              {logo}
            </Hidden>
            <Hidden marginBottom={2} device="laptop">
              <DSText
                component="h1"
                variant="h4"
                color="primary"
                style={{ whiteSpace: 'pre-line' }}
              >
                {t('signin.title')}
              </DSText>
            </Hidden>
            <form onSubmit={handleSubmit} data-testid="form">
              <FormControl fullWidth>
                <FacebookButton
                  label={t('signin.button.facebook')}
                  onCompleted={handleFacebookLogin}
                />
              </FormControl>
              {providerNotification?.key && (
                <DSText
                  component="p"
                  variant="body2"
                  color="warning"
                  id="provider-error"
                  data-testid="providerNotification"
                  dangerouslySetInnerHTML={{ __html: sanitize(providerNotification.message) }}
                  style={{ backgroundColor: '#ffd04d', padding: '10px' }}
                />
              )}
              <Divider>
                <DividerText variant="body2" color="textPrimary" component="span">
                  {t('signin.divider')}
                </DividerText>
                <DividerLine />
              </Divider>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  inputProps={{ required: true }}
                  type="email"
                  id="email"
                  name="email"
                  data-testid="formEmail"
                  label={t('signup.form.email')}
                  value={state.email}
                  onChange={handleEmailChange}
                  variant="outlined"
                  autoComplete="email"
                  margin="normal"
                  autoFocus
                  error={
                    state.emailValidation?.emailInvalidError ||
                    state.emailValidation?.emailUnconfirmedError
                  }
                />
                {state.emailValidation?.emailInvalidError && (
                  <FormHelperText error id="email-error" data-testid="email-invalid-error-message">
                    {t('errors.email-invalid')}
                  </FormHelperText>
                )}

                {state.emailValidation?.emailUnconfirmedError && (
                  <FormHelperText
                    error
                    id="email-error"
                    data-testid="email-unconfirmed-error-message"
                  >
                    {t('errors.email-unconfirmed')}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  label={`${t('signup.form.password')}`}
                  variant="outlined"
                  InputProps={{
                    required: true,
                    endAdornment: (
                      <InputAdornment>
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {state.showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  id="password"
                  name="password"
                  data-testid="formPassword"
                  type={state.showPassword ? 'text' : 'password'}
                  value={state.password}
                  onChange={handlePasswordChange}
                  error={state.passwordValidation?.passwordInvalidError}
                />
                {state.passwordValidation?.passwordInvalidError && (
                  <FormHelperText
                    error
                    id="password-error"
                    data-testid="password-invalid-error-message"
                  >
                    {t('errors.password-invalid')}
                  </FormHelperText>
                )}
              </FormControl>
              <CheckboxContainer>
                <Checkbox
                  color="secondary"
                  size="medium"
                  margin="none"
                  checked={state.persistent}
                  onChange={handlePersistentChange}
                />
                <DSText variant="caption" weight={400}>
                  {t('signup.form.persistent')}
                </DSText>
                <Tip informations={`${t('signup.form.persistent_tip')}`} />
              </CheckboxContainer>
              <FormControl fullWidth>
                <Button
                  mt={2}
                  type="submit"
                  id="btnSubmit"
                  data-testid="formSubmit"
                  color="primary"
                  fullWidth
                  endIcon={loadingSubmit && <CircularLoader scale="0.4" />}
                >
                  {!loadingSubmit && t('signin.button.signin')}
                </Button>
              </FormControl>
            </form>
            <Hidden invert device="laptop">
              {signUpBox}
            </Hidden>
            <Hidden py={2} device="laptop" />
            <LinkButton href="/accounts/forgot_password" variant="button" color="secondary">
              {t('signin.forgot-password')}
            </LinkButton>
            <Box py={1} />
            <LinkButton href="/accounts/confirmation/new" variant="button" color="secondary">
              {t('signin.unconfirmed-account')}
            </LinkButton>
          </Card>
        </Section>
      </Container>
      {notification?.key && (
        <Notification
          index={notification.key}
          message={notification.message}
          button={notification.button}
        />
      )}
      <Captcha ref={captchaRef} />
    </>
  );
}
